/**
 * The data provider's `leads` resource: Prospect tasks, read with their detail fields and
 * written through the task routes (writable on the live API). See leads.ts for the mapping.
 */
import type { Identifier } from "ra-core";

import { getSession, ziFetch, ziFetchAll } from "./client";
import { TASK_TYPES } from "./crmConfig";
import {
  buildLeadWrites,
  isLeadTask,
  leadToTaskPatch,
  taskToLead,
  type LeadInput,
  type ZcrmLead,
} from "./leads";
import type { ZcrmDeal, ZiTask } from "./mappers";
import { publish } from "./events";
import type { MeetingType } from "./bookingConfig";
import { buildMeetingWrites, type BookingAnswers, type Slot } from "./booking";

/** The leads inbox shows open leads plus those closed in this many days. */
const LEADS_LOOKBACK_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;
const DETAIL_CONCURRENCY = 6;

interface LeadDeps {
  cached: <T>(key: string, load: () => Promise<T>) => Promise<T>;
  invalidate: (prefix: string) => void;
  getTaskDetail: (task: ZiTask) => Promise<ZiTask>;
  createDeal: (data: Partial<ZcrmDeal>) => Promise<ZcrmDeal>;
}

const inBatches = async <T, R>(
  items: T[],
  size: number,
  worker: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += size) {
    results.push(...(await Promise.all(items.slice(i, i + size).map(worker))));
  }
  return results;
};

export const createLeadMethods = ({
  cached,
  invalidate,
  getTaskDetail,
  createDeal,
}: LeadDeps) => {
  const loadLeads = (): Promise<ZcrmLead[]> =>
    cached("leads", async () => {
      const since = new Date(Date.now() - LEADS_LOOKBACK_DAYS * DAY_MS)
        .toISOString()
        .slice(0, 10);
      const [open, recent] = await Promise.all([
        ziFetchAll<ZiTask>("/tasks/", { Type: TASK_TYPES.lead, active: true }),
        ziFetchAll<ZiTask>("/tasks/", {
          Type: TASK_TYPES.lead,
          updated_after: since,
        }),
      ]);
      const tasks = [
        ...new Map([...open, ...recent].map((t) => [t.id, t])).values(),
      ].filter(isLeadTask);
      const details = await inBatches(tasks, DETAIL_CONCURRENCY, getTaskDetail);
      return details
        .map(taskToLead)
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
    });

  const fetchLead = async (id: Identifier): Promise<ZcrmLead> =>
    taskToLead(await ziFetch<ZiTask>(`/tasks/${id}/`));

  const createLead = async (input: LeadInput): Promise<ZcrmLead> => {
    const { create, edit } = buildLeadWrites(input, getSession()?.user.id);
    const created = await ziFetch<{ id?: number; pk?: number }>(
      "/task/create/",
      { method: "POST", body: create },
    );
    const id = created?.id ?? created?.pk;
    if (!id) throw new Error("zInspector saved the lead but returned no id.");
    await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: edit });
    invalidate("leads");
    const lead = await fetchLead(id);
    publish("lead.created", { leadId: lead.id, source: lead.source });
    return lead;
  };

  const updateLead = async (
    id: Identifier,
    data: LeadInput,
    previous: Partial<ZcrmLead>,
  ): Promise<ZcrmLead> => {
    const patch = leadToTaskPatch(data, previous);
    if (Object.keys(patch).length > 0) {
      await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: patch });
      invalidate("leads");
    }
    if (data.status === "disqualified" && previous.status !== "disqualified") {
      publish("lead.disqualified", {
        leadId: id,
        reason: data.disqualify_reason ?? "",
      });
    }
    return fetchLead(id);
  };

  /** Creates the Sales deal at "New lead", then marks the lead qualified. */
  const convertLead = async (
    lead: ZcrmLead,
    deal: Partial<ZcrmDeal>,
  ): Promise<{ dealId: Identifier }> => {
    const created = await createDeal(deal);
    await updateLead(lead.id, { status: "qualified" }, lead);
    invalidate("companies");
    publish("lead.qualified", { leadId: lead.id, dealId: created.id });
    return { dealId: created.id };
  };

  /** Task edit takes the type by name: the lead becomes a Support task. */
  const moveLeadToHelpDesk = async (id: Identifier): Promise<void> => {
    await ziFetch(`/task/edit/${id}/`, {
      method: "PATCH",
      body: { Action: TASK_TYPES.ticket },
    });
    invalidate("leads");
    invalidate("tickets:");
  };

  /**
   * Books a meeting for a lead: a Follow-up task on the host's calendar, then the lead moves to
   * Contacted with the meeting as its next step, owned by the host.
   */
  const bookMeeting = async (
    lead: ZcrmLead,
    type: MeetingType,
    slot: Slot,
    answers: BookingAnswers,
  ): Promise<{ meetingId: Identifier }> => {
    const { create, edit } = buildMeetingWrites(type, slot, lead, answers);
    const created = await ziFetch<{ id?: number; pk?: number }>(
      "/task/create/",
      { method: "POST", body: create },
    );
    const id = created?.id ?? created?.pk;
    if (!id)
      throw new Error("zInspector booked the meeting but returned no id.");
    await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: edit });
    await updateLead(
      lead.id,
      {
        status: "contacted",
        replied_at: new Date().toISOString(),
        next_step: type.nextStep,
        reply_due: slot.start,
        sales_id: slot.hostId,
      },
      lead,
    );
    invalidate("activities:");
    publish("meeting.booked", {
      meetingId: id,
      leadId: lead.id,
      hostId: slot.hostId,
      startsAt: slot.start,
    });
    return { meetingId: id };
  };

  return {
    bookMeeting,
    loadLeads,
    fetchLead,
    createLead,
    updateLead,
    convertLead,
    moveLeadToHelpDesk,
  };
};
